import {
  type BoardSettingsFragment,
  SortOrder,
  TaskBoardSettingsDocument,
  type TaskBoardSettingsQuery,
  type TaskBoardSettingsQueryVariables,
  TaskOrderByColumnEnum,
  UpdateTaskBoardSettingsDocument,
  type UpdateTaskBoardSettingsInput,
  type UpdateTaskBoardSettingsMutation,
  type UpdateTaskBoardSettingsMutationVariables,
} from "@src/__generated__/graphql";
import { BOARD_SORT_COMMIT_DELAY_MS } from "@src/components/modules/resource-planning/tasks/kanban/constants";
import { FetchHelper } from "@src/helpers/apollo/fetch";
import { MutationHelper } from "@src/helpers/apollo/mutation";
import { debounce } from "lodash";
import { action, computed, makeObservable, observable } from "mobx";

export type TaskBoardSort = {
  column: TaskOrderByColumnEnum;
  direction: SortOrder;
};

export const DEFAULT_BOARD_SORT: TaskBoardSort = {
  column: TaskOrderByColumnEnum.BoardOrder,
  direction: SortOrder.Asc,
};

type CachedBoardSettings = {
  hiddenColumns: string[];
  collapsedColumns: string[];
  sort: TaskBoardSort;
};

type TaskBoardSettingsHandlers = {
  onSortChange: () => void;
  onColumnShown: (columnKey: string) => void;
};

export class TaskBoardSettingsStore {
  @observable.ref
  private boardKey = "";
  @observable.ref
  hiddenColumns: string[] = [];
  @observable.ref
  collapsedColumns: string[] = [];
  @observable.ref
  sort: TaskBoardSort = DEFAULT_BOARD_SORT;
  /**
   * A sort was picked and its reload has not started yet — the columns dim
   * from the click on, and their own reload state takes over from there.
   */
  @observable.ref
  isSortPending = false;

  private cache = new Map<string, CachedBoardSettings>();

  private settingsFetcher = new FetchHelper<
    TaskBoardSettingsQuery,
    TaskBoardSettingsQueryVariables
  >(TaskBoardSettingsDocument);

  private updateSettingsMutator = new MutationHelper<
    UpdateTaskBoardSettingsMutation,
    UpdateTaskBoardSettingsMutationVariables
  >(UpdateTaskBoardSettingsDocument);

  /**
   * A burst of sort clicks reloads the cards and saves once, after the last
   * click — the server runs every request the client drops, so reloading on
   * each click queued them up and the board kept loading.
   */
  private reloadSortedCards = debounce(
    // One action, so the columns are already reloading when pending clears.
    action(() => {
      this.isSortPending = false;
      this.handlers.onSortChange();
    }),
    BOARD_SORT_COMMIT_DELAY_MS,
  );
  private saveSort = debounce(
    (boardKey: string, sort: TaskBoardSort) =>
      this.saveSettings({ sort }, boardKey),
    BOARD_SORT_COMMIT_DELAY_MS,
  );

  constructor(private readonly handlers: TaskBoardSettingsHandlers) {
    makeObservable(this);
  }

  @computed
  get isManualSort(): boolean {
    return this.sort.column === TaskOrderByColumnEnum.BoardOrder;
  }

  isHidden(columnKey: string): boolean {
    return this.hiddenColumns.includes(columnKey);
  }

  isCollapsed(columnKey: string): boolean {
    return this.collapsedColumns.includes(columnKey);
  }

  @action.bound
  async fetch(boardKey: string): Promise<void> {
    if (boardKey !== this.boardKey) {
      // The new board loads its cards itself; the old one keeps its sort.
      this.reloadSortedCards.cancel();
      this.isSortPending = false;
      this.saveSort.flush();
    }
    this.boardKey = boardKey;

    const cached = this.cache.get(boardKey);
    if (cached) {
      this.applyCached(cached);
      return;
    }

    this.resetToDefaults();

    const [data, error] = await this.settingsFetcher.fetch({ boardKey });
    if (error) return;
    if (!data.taskBoardSettings) return;
    if (data.taskBoardSettings.board_key !== this.boardKey) return;

    this.applySettings(data.taskBoardSettings);
    this.writeCache();
  }

  @action.bound
  toggleHiddenColumn(columnKey: string): void {
    const isNowHidden = !this.isHidden(columnKey);

    this.hiddenColumns = isNowHidden
      ? [...this.hiddenColumns, columnKey]
      : this.hiddenColumns.filter((key) => key !== columnKey);
    this.writeCache();
    this.saveSettings({ hidden_columns: this.hiddenColumns });
    if (!isNowHidden) this.handlers.onColumnShown(columnKey);
  }

  @action.bound
  toggleCollapsedColumn(columnKey: string): void {
    const collapsedColumns = this.isCollapsed(columnKey)
      ? this.collapsedColumns.filter((key) => key !== columnKey)
      : [...this.collapsedColumns, columnKey];

    this.collapsedColumns = collapsedColumns;
    this.writeCache();
    this.saveSettings({ collapsed_columns: collapsedColumns });
  }

  @action.bound
  showAllColumns(): void {
    const shownKeys = [...this.hiddenColumns];

    this.hiddenColumns = [];
    this.writeCache();
    this.saveSettings({ hidden_columns: [] });

    for (const key of shownKeys) {
      this.handlers.onColumnShown(key);
    }
  }

  @action.bound
  setSort(column: TaskOrderByColumnEnum): void {
    const isSameColumn = column === this.sort.column;

    if (isSameColumn && column === TaskOrderByColumnEnum.BoardOrder) return;

    const direction =
      isSameColumn && this.sort.direction === SortOrder.Asc
        ? SortOrder.Desc
        : SortOrder.Asc;

    this.sort = { column, direction };

    this.writeCache();
    if (this.boardKey) this.saveSort(this.boardKey, this.sort);
    this.isSortPending = true;
    this.reloadSortedCards();
  }

  @action
  private applySettings(settings: BoardSettingsFragment): void {
    this.hiddenColumns = [...settings.hidden_columns];
    this.collapsedColumns = [...settings.collapsed_columns];
    this.sort = settings.sort
      ? {
          column: settings.sort.column as TaskOrderByColumnEnum,
          direction: settings.sort.direction,
        }
      : DEFAULT_BOARD_SORT;
  }

  @action
  private applyCached(cached: CachedBoardSettings): void {
    this.hiddenColumns = cached.hiddenColumns;
    this.collapsedColumns = cached.collapsedColumns;
    this.sort = cached.sort;
  }

  private writeCache(): void {
    if (!this.boardKey) return;

    this.cache.set(this.boardKey, {
      hiddenColumns: this.hiddenColumns,
      collapsedColumns: this.collapsedColumns,
      sort: {
        column: this.sort.column,
        direction: this.sort.direction,
      },
    });
  }

  @action
  private resetToDefaults(): void {
    this.hiddenColumns = [];
    this.collapsedColumns = [];
    this.sort = DEFAULT_BOARD_SORT;
  }

  private async saveSettings(
    input: Omit<UpdateTaskBoardSettingsInput, "board_key">,
    boardKey = this.boardKey,
  ): Promise<void> {
    if (!boardKey) return;

    await this.updateSettingsMutator.mutate({
      input: { board_key: boardKey, ...input },
    });
  }
}
